import React, { useCallback, useEffect, useState } from 'react';
import AppHeader from '../layouts/AppHeader';
import { listCharterLeads, getCharterLead, approveCharterLead, editCharterLeadQuote } from './charterDispatchApi';
import { IconCheck, IconRefresh, IconUser, IconBot, IconWarning, IconSearch } from '../ui/icons';
import { cx } from '../ui/cx';
import { stripAgentTokens } from '../components/ChatMessage';
import { humanizeRecurrenceDays, fmtClock } from './shuttleFormat';

const CHARTER_LOGO = '/peoria-charter-logo.svg';
const REFRESH_MS = 15000;

// Compact human duration for an event gap: 180 -> "3h", 150 -> "2h 30m", 45 -> "45m".
function fmtGap(mins) {
  const m = Math.max(0, Math.round(mins || 0));
  const h = Math.floor(m / 60);
  const r = m % 60;
  if (h && r) return `${h}h ${r}m`;
  if (h) return `${h}h`;
  return `${r}m`;
}

// Render parsed staggered pickups as an arrow chain: "5pm hotel → 5:30pm dorm → 6pm venue".
function fmtPickups(pickups) {
  return (pickups || [])
    .map((p) => `${p.time || ''}${p.location ? ` ${p.location}` : ''}`.trim())
    .filter(Boolean)
    .join(' → ');
}

// Shuttle recurrence tokens + the 12-hour window clock come from the shared shuttleFormat helpers
// (humanizeRecurrenceDays / fmtClock) so this summary matches the customer card's rendering.

// Shuttle "YYYY-MM-DD" service range → "Jun 2 – 13" / "Jun 30 – Jul 4" / "Jun 2" (single day).
// Year shown only when it isn't the current year. Non-ISO input passes through raw (fmtWhen rule).
function fmtServiceRange(start, end) {
  const parse = (s) => (/^\d{4}-\d{2}-\d{2}$/.test(String(s || '').trim()) ? new Date(`${s}T00:00`) : null);
  const a = parse(start);
  if (!a) return [start, end].filter(Boolean).join(' – ') || null;
  const b = parse(end) || a;
  const showYear = a.getFullYear() !== new Date().getFullYear() || b.getFullYear() !== a.getFullYear();
  const md = (d) => d.toLocaleDateString(undefined, { month: 'short', day: 'numeric', ...(showYear ? { year: 'numeric' } : {}) });
  if (b.getTime() === a.getTime()) return md(a);
  if (a.getFullYear() === b.getFullYear() && a.getMonth() === b.getMonth()) return `${md(a)} – ${b.getDate()}`;
  return `${md(a)} – ${md(b)}`;
}

// Human date-time for a captured slot. ISO-shaped strings ("2026-07-09T16:00") get locale
// formatting; planner free text ("tomorrow at 4 pm") passes through raw. The ISO guard is
// deliberate — free text must never reach new Date(), which half-parses some phrases.
function fmtWhen(raw) {
  if (raw == null || raw === '') return null;
  const s = String(raw).trim();
  if (/^\d{4}-\d{2}-\d{2}/.test(s)) {
    const hasTime = s.includes('T');
    const d = new Date(hasTime ? s : `${s}T00:00`);   // pin date-only to local midnight
    if (!Number.isNaN(d.getTime())) {
      return d.toLocaleString(undefined, {
        weekday: 'short', month: 'short', day: 'numeric',
        ...(d.getFullYear() !== new Date().getFullYear() ? { year: 'numeric' } : {}),
        ...(hasTime ? { hour: 'numeric', minute: '2-digit' } : {}),
      });
    }
  }
  return s;
}

// Charter-v2: the ops dashboard's editable "Information captured" — every captured slot grouped into
// logical sections, each field an input seeded from state_json.values. `slot` is the planner slot id
// (seed source, snake_case); `dto` is the CharterDispatchEditRequest key (camelCase) the Save posts;
// `type` drives the input (text | number | textarea). Saving a section re-prices through the same
// /dispatch/leads/{id}/edit endpoint (the Java side overlays pricing inputs + writes the rest back
// into state_json). Contact + occasion are NOT pricing inputs — the Java write-back + denormalized
// columns handle them.
const EDIT_SECTIONS = [
  { title: 'Trip Overview', fields: [
    { slot: 'event_type', dto: 'eventType', label: 'Event type' },
    { slot: 'trip_type', dto: 'tripType', label: 'Trip type' },
    { slot: 'passenger_count', dto: 'passengerCount', label: 'Passengers', type: 'number' },
  ] },
  { title: 'Contact', fields: [
    { slot: 'group_name', dto: 'groupName', label: 'Group / organization' },
    { slot: 'contact_name', dto: 'contactName', label: 'Contact name' },
    { slot: 'phone', dto: 'contactPhone', label: 'Phone' },
    { slot: 'email', dto: 'contactEmail', label: 'Email' },
  ] },
  // The itinerary is no longer a flat leg-1-only block — it's edited PER LEG by LegEditor below (every
  // leg's cities, states, pickup address, venue, departure, and stops). Only the trip-level overnight
  // scalars remain here as ordinary re-priced fields.
  { title: 'Overnight', fields: [
    { slot: 'overnight_city', dto: 'overnightCity', label: 'Overnight city' },
    { slot: 'overnight_nights', dto: 'overnightNights', label: 'Overnight nights', type: 'number' },
  ] },
  { title: 'Operations', fields: [
    { slot: 'airline', dto: 'airline', label: 'Airline' },
    { slot: 'flight_number', dto: 'flightNumber', label: 'Flight #' },
    { slot: 'flight_datetime', dto: 'flightDateTime', label: 'Flight time (ISO)' },
    { slot: 'terminal', dto: 'terminal', label: 'Terminal' },
    { slot: 'wheelchair_count', dto: 'numWheelchairs', label: 'Wheelchairs', type: 'number' },
    { slot: 'accessibility_notes', dto: 'accessibilityNotes', label: 'Accessibility notes' },
    { slot: 'hotel_arrangement', dto: 'hotelArrangement', label: 'Driver hotel' },
  ] },
  { title: 'Dispatcher Notes', fields: [
    { slot: 'comments', dto: 'tripNotes', label: 'Comments', type: 'textarea' },
    { slot: 'requested_driver', dto: 'requestedDriver', label: 'Requested driver' },
  ] },
];

const KNOB_LABELS = {
  BASE_RATE_PLACEHOLDER: 'Base charter rate',
  FUEL_SURCHARGE: 'Fuel surcharge',
  ALCOHOL_SURCHARGE: 'Alcohol contract fee',
  DRIVER_HOTEL: 'Driver hotel',
  TOLLS: 'Tolls',
  AIRPORT_FEE: 'Airport fee',
  TRAILER_FEE: 'Cargo trailer',
  ACCESSIBILITY_FEE: 'Lift-equipped coach',
  PARKING_PERMIT: 'Parking / permit',
  DEPOSIT: 'Deposit',
  SHUTTLE_MIN_HOURS: 'Shuttle daily minimum hours',
  MIN_FARE_FLOOR: 'Minimum fare floor',
};

const money = (v) => (v == null ? null : `$${Number(v).toFixed(2)}`);
// Round to cents for seeding the editable line-item inputs.
const round2 = (v) => Math.round(Number(v || 0) * 100) / 100;
// Parse a line-item input to a finite number (blank / garbage -> 0).
const toNum = (v) => {
  const n = Number(v);
  return Number.isFinite(n) ? n : 0;
};
// Deposit is a flat 15% of the (possibly dispatcher-edited) quote total.
const DEPOSIT_RATE = 0.15;

// The three canonical line items the ops card shows. Everything that isn't the base rate, the fuel
// surcharge, or the deposit collapses into a single editable "Extra fees" number.
const CORE_KNOBS = new Set(['BASE_RATE_PLACEHOLDER', 'FUEL_SURCHARGE', 'DEPOSIT']);

function parseState(lead) {
  try {
    return lead && lead.state ? JSON.parse(lead.state) : {};
  } catch (e) {
    return {};
  }
}

// The draft quote to render: prefer the persisted snapshot (which a dispatcher re-price updates), and
// fall back to the planner's last draft quote in state_json. They're equivalent for an un-edited lead
// (the agent sets the snapshot from the same last_quote), so this only diverges after a dispatcher edit.
function leadQuote(lead) {
  if (lead && lead.quoteSnapshotJson) {
    try {
      return JSON.parse(lead.quoteSnapshotJson);
    } catch (e) {
      /* fall through to the planner state */
    }
  }
  return parseState(lead).last_quote || null;
}

// ── leads rail ────────────────────────────────────────────────────────────────
function LeadsRail({ leads, selectedId, onSelect, loading, query, onQuery, total }) {
  const filtering = query.trim().length > 0;
  return (
    <aside className="w-72 shrink-0 border-r border-peoria-border bg-peoria-surface overflow-y-auto flex flex-col">
      <div className="px-4 py-3 border-b border-peoria-border">
        <div className="flex items-center justify-between mb-2">
          <span className="text-11 font-semibold uppercase tracking-brand text-peoria-muted">Quote requests</span>
          <span className="text-11 font-mono text-peoria-muted">{filtering ? `${leads.length}/${total}` : total}</span>
        </div>
        <div className="relative">
          <IconSearch size={14} className="absolute left-2.5 top-1/2 -translate-y-1/2 text-peoria-muted pointer-events-none" />
          <input
            type="search"
            value={query}
            onChange={(e) => onQuery(e.target.value)}
            placeholder="Search name, route, status…"
            aria-label="Search quote requests"
            className="w-full text-13 rounded-btn border border-peoria-border bg-peoria-surface2 pl-8 pr-2.5 py-1.5 focus:outline-none focus:ring-1 focus:ring-peoria-red/40 focus:border-peoria-red/60"
          />
        </div>
      </div>
      {leads.length === 0 && !loading && (
        <p className="px-4 py-6 text-13 text-peoria-muted">{filtering ? 'No matches.' : 'No charter leads yet.'}</p>
      )}
      <ul className="flex-1">
        {leads.map((l) => (
          <li key={l.sessionId}>
            <button
              onClick={() => onSelect(l.sessionId)}
              className={cx(
                'w-full text-left px-4 py-3 border-b border-peoria-border/60 border-l-2 transition-colors',
                l.sessionId === selectedId ? 'border-l-peoria-red bg-peoria-red/5' : 'border-l-transparent hover:bg-peoria-surface2',
              )}
            >
              <div className="flex items-center justify-between gap-2">
                <span className="text-13 font-semibold text-peoria-text truncate">
                  {l.groupName || 'New inquiry'}
                </span>
                <StatusPill status={l.status} />
              </div>
              <div className="text-11 text-peoria-muted truncate mt-0.5">{l.summary || '—'}</div>
              {(l.totalAmount != null || l.quoteStatus === 'PARTIAL' || l.quoteNumber) && (
                <div className="flex items-center gap-2 mt-1">
                  {l.quoteNumber && (
                    <span className="text-10 font-mono text-peoria-red">{l.quoteNumber}</span>
                  )}
                  {l.totalAmount != null && (
                    <span className="text-11 font-mono text-peoria-muted">{money(l.totalAmount)}</span>
                  )}
                  {l.quoteStatus === 'PARTIAL' && (
                    <span className="text-10 text-amber-700 font-medium">conditional</span>
                  )}
                </div>
              )}
            </button>
          </li>
        ))}
      </ul>
    </aside>
  );
}

function StatusPill({ status }) {
  const map = {
    DRAFT: 'bg-peoria-red/10 text-peoria-red border-peoria-red/30',
    QUOTED: 'bg-blue-500/10 text-blue-700 border-blue-500/30',
    PARTIAL: 'bg-amber-500/10 text-amber-700 border-amber-500/30',
    SUBMITTED: 'bg-green-500/10 text-green-700 border-green-500/30',
    BOOKED: 'bg-green-500/10 text-green-700 border-green-500/30',
    NEEDS_DISPATCH: 'bg-danger/10 text-red-600 border-danger/40',
  };
  return (
    <span className={cx('text-10 font-medium px-2 py-0.5 rounded-full border shrink-0', map[status] || map.DRAFT)}>
      {(status || 'DRAFT').toLowerCase()}
    </span>
  );
}

// ── captured info — every slot grouped + EDITABLE (Charter-v2) ────────────────────
// One unified card (the standalone "Customer" card is gone — contact lives here now). Each section is
// a form of the captured slots; Save posts only the CHANGED fields as a CharterDispatchEditRequest and
// re-prices (pricing edits) / writes back to state_json (the rest). Seeded from state_json.values;
// re-seeds when the lead changes (the panel is keyed by the lead in LeadDetail).
const EDIT_INPUT_SM = 'w-full text-13 text-peoria-text border border-peoria-border rounded-btn px-2 py-1 bg-peoria-surface2 focus:outline-none focus:ring-1 focus:ring-peoria-red/40';

// Charter-v2: the dashboard must show the FULL multi-leg itinerary (leg 1 unprefixed + every leg{k}_*
// family), so a dispatcher sees the SAME trip the customer built — not just leg 1. Mirrors the agents
// planner's _num_legs: the highest leg with any city present, floored by leg_count / a two-leg shape.
const MAX_LEGS = 6;
function legCount(values) {
  let highest = 1;
  for (let k = 2; k <= MAX_LEGS; k++) {
    if (values[`leg${k}_departure_city`] || values[`leg${k}_destination_city`] || values[`leg${k}_destination_detail`]) {
      highest = k;
    }
  }
  // Any 2+ leg itinerary is multi_leg; the leg count comes from leg_count or the highest leg{k}_* family.
  return Math.max(Number(values.leg_count) || 0, highest, 1);
}
// Per-leg field accessor: leg 1 is unprefixed, legs 2+ are leg{k}_ prefixed.
const legField = (values, k, name) => values[`${k === 1 ? '' : `leg${k}_`}${name}`] || '';

// Read-only per-leg itinerary summary — the single source of truth both the customer card and this
// dashboard render from. Reads directly from state.values (always present, even on a PARTIAL/pending
// quote), so no leg silently disappears just because it's leg 2 or leg 3.
function ItineraryLegs({ values }) {
  const n = legCount(values);
  const isRoundTrip = String(values.trip_type || '').toLowerCase() === 'round_trip';
  // City/state is the PRIMARY line (the priced route); the place name / pickup address is the
  // SECONDARY line shown underneath when captured. When only a name exists (no city/state), the name
  // stands in as the primary so a leg never renders as "— → —".
  const cityState = (k, side) =>
    [legField(values, k, `${side}_city`), legField(values, k, `${side}_state`)].filter(Boolean).join(', ');
  const rows = [];
  for (let k = 1; k <= n; k++) {
    const fromCity = cityState(k, 'departure');
    const toCity = cityState(k, 'destination');
    const fromName = legField(values, k, 'pickup_address');
    const toName = legField(values, k, 'destination_detail');
    if (!fromCity && !toCity && !fromName && !toName) continue;
    const depart = fmtWhen(legField(values, k, 'departure_datetime'));
    // The leg-1 return datetime shows on its OWN return leg row for a round trip (below); only a
    // legacy same-cities round trip (no explicit return leg) still annotates it inline.
    const ret = (!isRoundTrip && k <= 2)
      ? fmtWhen(k === 1 ? values.return_datetime : values.leg2_return_datetime) : null;
    rows.push({ k, fromCity, toCity, fromName, toName, depart, ret, stops: legField(values, k, 'stops') });
  }
  // ROUND TRIP: the return leg lives in the return_* slot family (never leg{k}_*), so add it explicitly
  // — otherwise the dispatcher only ever sees the outbound leg. `from` = the return PICKUP (which may
  // differ from the outbound drop-off, e.g. a hotel); `to` = the original departure (locked).
  if (isRoundTrip) {
    const rFromCity = [values.return_pickup_city, values.return_pickup_state].filter(Boolean).join(', ');
    const rToCity = [values.departure_city, values.departure_state].filter(Boolean).join(', ');
    const rFromName = values.return_pickup_address;
    const rToName = values.return_destination_detail || values.pickup_address;
    if (rFromCity || rToCity || rFromName || rToName) {
      rows.push({ k: rows.length + 1, fromCity: rFromCity, toCity: rToCity, fromName: rFromName, toName: rToName,
                  depart: fmtWhen(values.return_datetime), ret: null, stops: values.return_stops, isReturn: true });
    }
  }
  if (rows.length === 0) return null;
  return (
    <div>
      <div className="text-10 font-semibold text-peoria-muted uppercase tracking-brand mb-1.5">
        Itinerary{rows.length > 1 ? ` · ${rows.length} legs` : ''}
      </div>
      <ol className="space-y-1.5">
        {rows.map((r) => {
          // Primary = city/state (fall back to the name when no city/state was captured). Secondary =
          // the place name / address, shown only when it adds something beyond the primary line.
          const fromPrimary = r.fromCity || r.fromName;
          const toPrimary = r.toCity || r.toName;
          const fromDetail = r.fromCity ? r.fromName : '';
          const toDetail = r.toCity ? r.toName : '';
          const hasDetail = fromDetail || toDetail;
          return (
            <li key={r.k} className="rounded-btn border border-peoria-border bg-peoria-surface2/50 px-3 py-2">
              <div className="flex items-center justify-between">
                <span className="text-10 font-semibold uppercase tracking-brand text-peoria-text">
                  {r.isReturn ? 'Return' : `Leg ${r.k}`}
                </span>
                {r.depart && <span className="font-mono text-11 text-peoria-muted">{r.depart}</span>}
              </div>
              <div className="mt-1 text-13 text-peoria-text">
                <span className="font-medium">{fromPrimary || '—'}</span>
                <span className="text-peoria-muted"> → </span>
                <span className="font-medium">{toPrimary || '—'}</span>
              </div>
              {hasDetail && (
                <div className="mt-0.5 text-11 text-peoria-muted">
                  {fromDetail || '—'}<span className="text-peoria-muted"> → </span>{toDetail || '—'}
                </div>
              )}
              {r.stops && <div className="mt-0.5 text-11 text-peoria-muted">Via {r.stops}</div>}
              {r.ret && <div className="mt-0.5 text-11 text-peoria-muted">Return · {r.ret}</div>}
            </li>
          );
        })}
      </ol>
    </div>
  );
}

// Contact lives in the standalone Customer card, so it's dropped from the info-captured summary
// (but stays editable in the Edit form below). The route is edited PER LEG by LegEditor, so it isn't
// part of the scalar summary sections either.
const SUMMARY_SECTIONS = EDIT_SECTIONS.filter((s) => s.title !== 'Contact');

function fmtSlotValue(slot, val) {
  if (val == null || val === '') return null;
  if (String(slot).endsWith('datetime')) return fmtWhen(val) || String(val);
  return String(val);
}

function hasItinerary(values) {
  const n = legCount(values);
  for (let k = 1; k <= n; k++) {
    const from = legField(values, k, 'pickup_address') || legField(values, k, 'departure_city');
    const to = legField(values, k, 'destination_detail') || legField(values, k, 'destination_city');
    if (from || to) return true;
  }
  return false;
}

// Per-leg editable fields, in display order. `name` is the leg-relative slot suffix; the actual slot
// is unprefixed for leg 1 and `leg{k}_` for legs 2+ (see legEditGroups). This is the full capture set
// a dispatcher can correct on ANY leg: cities, states, pickup address, venue name, departure, stops.
const LEG_EDIT_FIELDS = [
  { name: 'departure_city', label: 'Departure city' },
  { name: 'departure_state', label: 'Departure state' },
  { name: 'pickup_address', label: 'Pickup address' },
  { name: 'destination_city', label: 'Destination city' },
  { name: 'destination_state', label: 'Destination state' },
  { name: 'destination_detail', label: 'Destination / venue' },
  { name: 'departure_datetime', label: 'Departure (ISO)' },
  { name: 'stops', label: 'Stops' },
];
// The round-trip return leg lives in the return_* slot family (never leg{k}_*), so it's edited with
// its own explicit slots.
const RETURN_EDIT_FIELDS = [
  { slot: 'return_pickup_city', label: 'Return pickup city' },
  { slot: 'return_pickup_state', label: 'Return pickup state' },
  { slot: 'return_pickup_address', label: 'Return pickup address' },
  { slot: 'return_destination_detail', label: 'Return destination / venue' },
  { slot: 'return_datetime', label: 'Return (ISO)' },
  { slot: 'return_stops', label: 'Return stops' },
];
// Leg-1 slots that ALSO map to a first-class pricing dto on the edit endpoint (so a leg-1 route edit
// re-prices, same as before). Everything else (legs 2+, return_*) persists via `slotOverrides`.
const LEG1_DTO = {
  departure_city: 'pickupCity',
  departure_state: 'pickupState',
  destination_city: 'dropoffCity',
  destination_state: 'dropoffState',
  pickup_address: 'pickupAddress',
  destination_detail: 'destinationDetail',
  departure_datetime: 'pickupDateTime',
  stops: 'stops',
  return_datetime: 'returnDateTime',
};

// The editable leg groups for a lead — mirrors ItineraryLegs so the dispatcher edits exactly the legs
// they see: legs 1..n (unprefixed leg 1, leg{k}_ for the rest) plus a round-trip Return leg.
function legEditGroups(values) {
  const n = legCount(values);
  const isRoundTrip = String(values.trip_type || '').toLowerCase() === 'round_trip';
  const groups = [];
  for (let k = 1; k <= n; k++) {
    groups.push({
      title: `Leg ${k}`,
      fields: LEG_EDIT_FIELDS.map((f) => ({ label: f.label, slot: `${k === 1 ? '' : `leg${k}_`}${f.name}` })),
    });
  }
  if (isRoundTrip) {
    groups.push({ title: 'Return', fields: RETURN_EDIT_FIELDS.map((f) => ({ label: f.label, slot: f.slot })) });
  }
  return groups;
}

function InformationCaptured({ values, onReprice, busy, editNote }) {
  // Read-only summary by default; a dispatcher clicks Edit to reveal the (unchanged) editable form.
  const [editing, setEditing] = useState(false);
  // Seeded ONCE from the lead's captured values. The panel is keyed by lead.sessionId in LeadDetail, so
  // switching leads remounts with fresh values; within a lead the draft persists across the 15s poll and
  // survives a failed (422) save. `values` is a fresh object each render (parseState re-parses), so we
  // must NOT re-seed on every render — that would clobber in-progress edits.
  const [draft, setDraft] = useState(() => ({ ...values }));

  const set = (slot, val) => setDraft((d) => ({ ...d, [slot]: val }));

  const saveSection = (section) => {
    const payload = {};
    for (const f of section.fields) {
      const next = draft[f.slot];
      const orig = values[f.slot];
      const changed = (next ?? '') !== (orig ?? '');
      if (!changed) continue;
      payload[f.dto] = f.type === 'number'
        ? (next === '' || next == null ? null : Number(next))
        : (next ?? '');
    }
    if (Object.keys(payload).length > 0) onReprice(payload);
  };

  const sectionDirty = (section) =>
    section.fields.some((f) => (draft[f.slot] ?? '') !== (values[f.slot] ?? ''));

  // Per-leg itinerary save: leg-1 route fields flow through their first-class pricing dtos (so the leg
  // re-prices as before); every other captured leg field (legs 2+, return_*) rides `slotOverrides`,
  // which the ops endpoint writes back into state_json verbatim so no leg silently drops its edit.
  const legGroups = legEditGroups(values);
  const legsDirty = legGroups.some((g) => g.fields.some((f) => (draft[f.slot] ?? '') !== (values[f.slot] ?? '')));
  const saveLegs = () => {
    const payload = {};
    const slotOverrides = {};
    for (const g of legGroups) {
      for (const f of g.fields) {
        if ((draft[f.slot] ?? '') === (values[f.slot] ?? '')) continue;
        const val = draft[f.slot] ?? '';
        if (LEG1_DTO[f.slot]) payload[LEG1_DTO[f.slot]] = val;
        else slotOverrides[f.slot] = val;
      }
    }
    if (Object.keys(slotOverrides).length > 0) payload.slotOverrides = slotOverrides;
    if (Object.keys(payload).length > 0) onReprice(payload);
  };

  // Read-only rows per scalar section (only filled fields). The route is shown by ItineraryLegs.
  const summary = SUMMARY_SECTIONS
    .map((section) => ({
      title: section.title,
      rows: section.fields
        .map((f) => ({ label: f.label, value: fmtSlotValue(f.slot, values[f.slot]) }))
        .filter((r) => r.value != null),
    }))
    .filter((s) => s.rows.length > 0);

  // READ-ONLY extras not in the editable field config: the round-trip driver/hotel plan (rt_coach_
  // disposition) and the alcohol answer — so the dispatcher sees the full captured picture. No Java dto.
  const truthy = (v) => v === true || ['true', 'yes', '1'].includes(String(v).toLowerCase());
  const extraRows = [];
  if (values.rt_coach_disposition) {
    extraRows.push({ label: 'Driver / hotel plan (between trips)', value: String(values.rt_coach_disposition) });
  }
  const alcoholVal = values.alcohol_agreement != null && values.alcohol_agreement !== ''
    ? values.alcohol_agreement : values.alcohol_onboard;
  if (alcoholVal != null && alcoholVal !== '') {
    extraRows.push({ label: 'Alcohol onboard',
      value: truthy(alcoholVal) ? 'Yes — signed agreement required' : 'No' });
  }
  if (extraRows.length) summary.push({ title: 'Between the trips', rows: extraRows });

  const nothingCaptured = summary.length === 0 && !hasItinerary(values);

  return (
    <section className="bg-peoria-surface border border-peoria-border rounded-card shadow-card p-5">
      <div className="flex items-center justify-between mb-3">
        <h2 className="text-15 font-semibold text-peoria-text">Information captured</h2>
        <button
          type="button"
          onClick={() => setEditing((e) => !e)}
          className={cx('text-11 font-medium rounded-btn px-2.5 py-1 border transition-colors',
            editing
              ? 'text-peoria-muted border-peoria-border hover:bg-peoria-surface2'
              : 'text-peoria-red border-peoria-red/30 hover:bg-peoria-red/5')}
        >
          {editing ? 'Done' : 'Edit'}
        </button>
      </div>
      {editNote && (
        <div className={cx('mb-3 rounded-card border px-3 py-2 text-12',
          editNote.type === 'ok' ? 'bg-green-500/10 border-green-500/30 text-green-700' : 'bg-danger/10 border-danger/40 text-red-600')}>
          {editNote.text}
        </div>
      )}
      {editing ? (
        <div className="space-y-5">
          {/* Per-leg itinerary editor — every captured leg (legs 1..n + a round-trip Return) with its
              own cities, states, pickup address, venue, departure, and stops. */}
          {legGroups.length > 0 && (
            <div>
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-10 font-semibold text-peoria-muted uppercase tracking-brand">Itinerary</div>
                {legsDirty && (
                  <button type="button" onClick={saveLegs} disabled={busy}
                    className="text-11 font-medium text-peoria-red border border-peoria-red/30 rounded-btn px-2 py-0.5 hover:bg-peoria-red/5 disabled:opacity-60">
                    {busy ? 'Saving…' : 'Save & re-price'}
                  </button>
                )}
              </div>
              <div className="space-y-3">
                {legGroups.map((g) => (
                  <div key={g.title} className="rounded-btn border border-peoria-border bg-peoria-surface2/40 px-3 py-2.5">
                    <div className="text-10 font-semibold uppercase tracking-brand text-peoria-text mb-1.5">{g.title}</div>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                      {g.fields.map((f) => (
                        <label key={f.slot} className="block">
                          <span className="text-11 text-peoria-muted">{f.label}</span>
                          <input aria-label={`${g.title} · ${f.label}`} className={EDIT_INPUT_SM}
                            value={draft[f.slot] ?? ''} onChange={(e) => set(f.slot, e.target.value)} />
                        </label>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}
          {EDIT_SECTIONS.map((section) => (
            <div key={section.title}>
              <div className="flex items-center justify-between mb-1.5">
                <div className="text-10 font-semibold text-peoria-muted uppercase tracking-brand">{section.title}</div>
                {sectionDirty(section) && (
                  <button type="button" onClick={() => saveSection(section)} disabled={busy}
                    className="text-11 font-medium text-peoria-red border border-peoria-red/30 rounded-btn px-2 py-0.5 hover:bg-peoria-red/5 disabled:opacity-60">
                    {busy ? 'Saving…' : 'Save & re-price'}
                  </button>
                )}
              </div>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
                {section.fields.map((f) => (
                  <label key={f.slot} className="block">
                    <span className="text-11 text-peoria-muted">{f.label}</span>
                    {f.type === 'textarea' ? (
                      <textarea aria-label={f.label} className={cx(EDIT_INPUT_SM, 'resize-y min-h-[2.2rem]')}
                        value={draft[f.slot] ?? ''} onChange={(e) => set(f.slot, e.target.value)} />
                    ) : (
                      <input aria-label={f.label} className={EDIT_INPUT_SM}
                        inputMode={f.type === 'number' ? 'numeric' : undefined}
                        value={draft[f.slot] ?? ''} onChange={(e) => set(f.slot, e.target.value)} />
                    )}
                  </label>
                ))}
              </div>
            </div>
          ))}
        </div>
      ) : nothingCaptured ? (
        <p className="text-13 text-peoria-muted">Nothing captured yet.</p>
      ) : (
        <div className="space-y-4">
          <ItineraryLegs values={values} />
          {summary.map((section) => (
            <div key={section.title}>
              <div className="text-10 font-semibold text-peoria-muted uppercase tracking-brand mb-1.5">{section.title}</div>
              <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-4 gap-y-2">
                {section.rows.map((r) => (
                  <div key={r.label} className="flex flex-col min-w-0">
                    <dt className="text-11 text-peoria-muted">{r.label}</dt>
                    <dd className="text-13 text-peoria-text font-medium break-words">{r.value}</dd>
                  </div>
                ))}
              </dl>
            </div>
          ))}
        </div>
      )}
    </section>
  );
}

// Compact customer card — group, contact, phone, email. Falls back to the lead's denormalized
// columns when the planner slot is empty.
function CustomerCard({ values, lead }) {
  const rows = [
    ['Group / organization', values.group_name || lead.groupName],
    ['Contact', values.contact_name || lead.contactName],
    ['Phone', values.phone || lead.contactPhone],
    ['Email', values.email || lead.contactEmail],
  ].filter(([, v]) => v != null && v !== '');
  return (
    <section className="bg-peoria-surface border border-peoria-border rounded-card shadow-card p-5">
      <h2 className="text-15 font-semibold text-peoria-text mb-3">Customer</h2>
      {rows.length === 0 ? (
        <p className="text-13 text-peoria-muted">No contact captured yet.</p>
      ) : (
        <dl className="space-y-2">
          {rows.map(([label, value]) => (
            <div key={label} className="flex items-baseline justify-between gap-3">
              <dt className="text-11 text-peoria-muted shrink-0">{label}</dt>
              <dd className="text-13 text-peoria-text font-medium text-right break-words min-w-0">{value}</dd>
            </div>
          ))}
        </dl>
      )}
    </section>
  );
}

// A "unique trip" (trip_type special_request) is a plain team handoff — no route, no quote, no schedule.
// The rider's free-form description is the whole ask, so surface it read-only for the dispatcher. The
// text may land as `special_description` in the parsed state values or the denormalized `specialDescription`.
function SpecialRequestCard({ values, lead }) {
  const text = (values && values.special_description) || (lead && lead.specialDescription);
  if (!text) return null;
  return (
    <section className="bg-peoria-surface border border-peoria-border rounded-card shadow-card p-5">
      <h2 className="text-15 font-semibold text-peoria-text mb-3">Trip details (unique request)</h2>
      <p className="text-13 text-peoria-text whitespace-pre-wrap leading-relaxed">{text}</p>
    </section>
  );
}

// Shared input styling for the editable line items in the draft quote card.
const EDIT_INPUT = 'w-full text-13 text-peoria-text border border-peoria-border rounded-btn px-2 py-1 bg-peoria-surface2 focus:outline-none focus:ring-1 focus:ring-peoria-red/40';

// ── recurring-shuttle summary (tripType "shuttle") ───────────────────────────────
// The dispatcher's digest of a shuttle quote snapshot: the loop route, the service dates/cadence,
// the daily window (or split out/back blocks), and the hourly billing basis
// (billableHoursPerDay × coaches — shuttle money is time-based, never mileage). Extra pickup
// points ride stopsStructured with purpose "pickup", so they render HERE instead of the generic
// Stops section. Renders above the line items; the knob aggregation/approve flow is untouched.
function ShuttleSummary({ quote }) {
  const side = (city, st) => [city, st].filter(Boolean).join(', ');
  const from = side(quote.pickupCity, quote.pickupState);
  const to = side(quote.dropoffCity, quote.dropoffState);
  const schedule = [
    fmtServiceRange(quote.serviceStartDate, quote.serviceEndDate),
    humanizeRecurrenceDays(quote.recurrenceDays),
    quote.serviceDays != null ? `${quote.serviceDays} service day${quote.serviceDays !== 1 ? 's' : ''}` : null,
  ].filter(Boolean).join(' · ');
  const coaches = quote.coachCount || 1;
  const extraPickups = (quote.stopsStructured || [])
    .filter((s) => String((s && s.purpose) || '').toLowerCase() === 'pickup')
    .map((s) => s.location)
    .filter(Boolean);
  return (
    <div className="mt-3">
      <div className="text-10 font-semibold text-peoria-muted uppercase tracking-brand mb-1">Shuttle service</div>
      <div className="text-13 text-peoria-text space-y-0.5">
        <div className="font-medium">{from || '—'} ↔ {to || '—'}</div>
        {schedule && <div>{schedule}</div>}
        {quote.splitWindows ? (
          <div className="text-peoria-muted">
            Out {fmtClock(quote.windowStart)} – {fmtClock(quote.outWindowEnd)} · Back {fmtClock(quote.backWindowStart)} – {fmtClock(quote.windowEnd)} (bus released between)
          </div>
        ) : (quote.windowStart || quote.windowEnd) ? (
          <div className="text-peoria-muted">Service window {fmtClock(quote.windowStart)} – {fmtClock(quote.windowEnd)} (continuous)</div>
        ) : null}
        {quote.billableHoursPerDay != null && (
          <div className="text-peoria-muted">
            {Number(quote.billableHoursPerDay)} hrs/day × {coaches} coach{coaches !== 1 ? 'es' : ''}
            {quote.loopMiles != null ? ` · ~${Math.round(Number(quote.loopMiles))} mi loop` : ''}
          </div>
        )}
        {extraPickups.length > 0 && (
          <div className="text-peoria-muted">Extra pickups: {extraPickups.join(' · ')}</div>
        )}
      </div>
    </div>
  );
}

// ── draft quote panel (base rate · fuel · extra fees — all editable) ─────────────
function DraftQuotePanel({ quote, editable, quoteNumber }) {
  // Line-item editing is client-side: a dispatcher tweaks the three numbers and the total + 15%
  // deposit recompute locally. `overrides` (once applied) replaces the priced amounts for display;
  // `draft` holds the in-progress input strings while editing. The panel is keyed by lead in
  // LeadDetail, so switching leads clears any override.
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(null);
  const [overrides, setOverrides] = useState(null);

  // A round-trip / multi-leg quote carries its line items PER LEG (no top-level appliedKnobs), so
  // aggregate across legs — otherwise the whole breakdown reads $0.00 for a combined quote (the
  // reported bug). A single-leg (one-way) quote keeps its top-level appliedKnobs, so this is a no-op
  // for it. Base rate + fuel are SUMMED across legs below; extras are concatenated.
  const knobs = quote
    ? (Array.isArray(quote.legs) && quote.legs.length
        ? quote.legs.flatMap((l) => l.appliedKnobs || [])
        : (quote.appliedKnobs || []))
    : [];
  const sumKnob = (name) => knobs.filter((k) => k.knob === name).reduce((s, k) => s + Number(k.amount || 0), 0);
  const extraKnobs = knobs.filter((k) => !CORE_KNOBS.has(k.knob));

  if (!quote) {
    return (
      <section className="bg-peoria-surface border border-peoria-border rounded-card shadow-card p-5">
        <h2 className="text-15 font-semibold text-peoria-text mb-2">Draft quote</h2>
        <p className="text-13 text-peoria-muted">No quote generated yet — still collecting the trip.</p>
      </section>
    );
  }

  // The three canonical amounts — dispatcher overrides win, else the priced knob values (summed across
  // all legs for a combined quote).
  const baseAmt = overrides ? overrides.base : round2(sumKnob('BASE_RATE_PLACEHOLDER'));
  const fuelAmt = overrides ? overrides.fuel : round2(sumKnob('FUEL_SURCHARGE'));
  const extraAmt = overrides ? overrides.extra : round2(extraKnobs.reduce((s, k) => s + Number(k.amount || 0), 0));
  const total = round2(baseAmt + fuelAmt + extraAmt);
  const deposit = round2(total * DEPOSIT_RATE);
  const extraDetail = extraKnobs.map((k) => KNOB_LABELS[k.knob] || k.knob).join(' · ');

  const startEdit = () => {
    setDraft({ base: String(baseAmt), fuel: String(fuelAmt), extra: String(extraAmt) });
    setEditing(true);
  };
  const applyEdit = () => {
    setOverrides({ base: round2(toNum(draft.base)), fuel: round2(toNum(draft.fuel)), extra: round2(toNum(draft.extra)) });
    setEditing(false);
  };
  const setField = (key, val) => setDraft((d) => ({ ...d, [key]: val }));

  const isShuttle = String(quote.tripType || '').toLowerCase() === 'shuttle';

  const header = (
    <div className="flex items-center justify-between mb-3">
      <h2 className="text-15 font-semibold text-peoria-text">
        Draft quote{(quoteNumber || quote.quoteNumber)
          ? <span className="font-mono text-13 text-peoria-muted"> {quoteNumber || quote.quoteNumber}</span>
          : null}
      </h2>
      <div className="flex items-center gap-2">
        {quote.pricePending && (
          <span className="text-10 font-medium px-2 py-0.5 rounded-full border bg-danger/10 text-red-600 border-danger/40">
            price pending
          </span>
        )}
        {quote.quoteStatus === 'PARTIAL' && (
          <span className="text-10 font-medium px-2 py-0.5 rounded-full border bg-amber-500/10 text-amber-700 border-amber-500/30">
            conditional
          </span>
        )}
        {editable && !editing && (
          <button
            type="button"
            onClick={startEdit}
            className="text-11 font-medium text-peoria-red border border-peoria-red/30 rounded-btn px-2 py-0.5 hover:bg-peoria-red/10"
          >
            Edit / re-price
          </button>
        )}
      </div>
    </div>
  );
  return (
    <section className="bg-peoria-surface border border-peoria-border rounded-card shadow-card p-5">
      {header}
      <FeasibilityBanner quote={quote} />
      <HosTimeline quote={quote} />

      {isShuttle && <ShuttleSummary quote={quote} />}

      {(quote.passengerCount != null || quote.coachCount > 1 || quote.numWheelchairs > 0) && (
        <div className="mt-3">
          <div className="text-10 font-semibold text-peoria-muted uppercase tracking-brand mb-1">Capacity</div>
          <div className="text-13 text-peoria-text">
            {quote.passengerCount != null && <span>{quote.passengerCount} passengers</span>}
            {quote.coachCount > 1 && <span className="text-peoria-muted"> · {quote.coachCount} coaches (auto-split)</span>}
            {quote.numWheelchairs > 0 && <span className="text-peoria-muted"> · {quote.numWheelchairs} wheelchair{quote.numWheelchairs !== 1 ? 's' : ''}</span>}
          </div>
        </div>
      )}

      {/* A shuttle's stopsStructured only carries its extra pickup points, which ShuttleSummary
          already renders — skip the generic Stops section so they don't show twice. */}
      {!isShuttle && Array.isArray(quote.stopsStructured) && quote.stopsStructured.length > 0 && (
        <div className="mt-3">
          <div className="text-10 font-semibold text-peoria-muted uppercase tracking-brand mb-1">
            Stops
            {quote.stopsOptimized && (
              <span className="normal-case font-normal text-peoria-text"> · optimized route order</span>
            )}
          </div>
          <ol className="space-y-0.5">
            {quote.stopsStructured.map((s, i) => (
              <li key={i} className="text-13 text-peoria-text">
                {i + 1}. {s.location}
                {(s.purpose || s.dwellMinutes != null) && (
                  <span className="text-peoria-muted">
                    {' — '}
                    {[s.purpose, s.dwellMinutes != null && `~${s.dwellMinutes} min`].filter(Boolean).join(', ')}
                  </span>
                )}
              </li>
            ))}
          </ol>
        </div>
      )}

      {quote.overnightNights > 0 && (
        <div className="mt-3">
          <div className="text-10 font-semibold text-peoria-muted uppercase tracking-brand mb-1">Overnight</div>
          <div className="text-13 text-peoria-text space-y-0.5">
            {Array.isArray(quote.overnightStays) && quote.overnightStays.length > 0 ? (
              <ol className="list-decimal list-inside space-y-0.5">
                {quote.overnightStays.map((s, i) => (
                  <li key={i}>
                    {s.city || 'overnight'}{s.nights ? ` · ${s.nights} night${s.nights !== 1 ? 's' : ''}` : ''}
                  </li>
                ))}
              </ol>
            ) : (
              <div>{quote.overnightNights} night{quote.overnightNights !== 1 ? 's' : ''}{quote.overnightCity ? ` · ${quote.overnightCity}` : ''}</div>
            )}
            <div className="text-peoria-muted">
              Driver hotel: {quote.hotelIncludedInQuote === false ? 'customer arranges' : 'included in quote'}
              {quote.hotelArrangement ? ` — “${quote.hotelArrangement}”` : ''}
            </div>
          </div>
        </div>
      )}

      {quote.destinationIsAirport && (
        <div className="mt-3">
          <div className="text-10 font-semibold text-peoria-muted uppercase tracking-brand mb-1">Flight</div>
          <div className="text-13 text-peoria-text space-y-0.5">
            <div>
              {quote.airportDirection === 'departure' ? 'Departure drop-off' : 'Arrival pickup'}
              {(quote.airline || quote.flightNumber) ? ` · ${[quote.airline, quote.flightNumber].filter(Boolean).join(' ')}` : ''}
            </div>
            {(quote.flightDateTime || quote.terminal) && (
              <div className="text-peoria-muted">
                {quote.flightDateTime || ''}{quote.terminal ? ` · Terminal ${quote.terminal}` : ''}
              </div>
            )}
            {quote.meetGreet && <div className="text-peoria-muted">Meet-greet: {quote.meetGreet}</div>}
            {quote.flightStatus && <div className="text-peoria-muted">Flight status: {quote.flightStatus}</div>}
          </div>
        </div>
      )}

      {(quote.venueTiming || quote.multiplePickupTimes || quote.bridalPartyPickup || quote.chaperones || quote.costCenter || quote.waitingWindowMode) && (
        <div className="mt-3">
          <div className="text-10 font-semibold text-peoria-muted uppercase tracking-brand mb-1">Event</div>
          <div className="text-13 text-peoria-text space-y-0.5">
            {quote.venueTiming && <div>{quote.venueTiming}</div>}
            {quote.waitingWindowMode && (
              <div className="text-peoria-muted">
                Between movements: {quote.waitingWindowMode}
                {quote.eventGapMinutes ? ` · ~${fmtGap(quote.eventGapMinutes)} on-site gap` : ''}
              </div>
            )}
            {quote.pickups && quote.pickups.length > 0 ? (
              <div className="text-peoria-muted">
                Pickups{quote.pickupWindowMinutes ? ` (${fmtGap(quote.pickupWindowMinutes)} window)` : ''}:{' '}
                {fmtPickups(quote.pickups)}
              </div>
            ) : quote.multiplePickupTimes ? (
              <div className="text-peoria-muted">Pickups: {quote.multiplePickupTimes}</div>
            ) : null}
            {quote.bridalPartyPickup && <div className="text-peoria-muted">Bridal party: {quote.bridalPartyPickup}</div>}
            {quote.chaperones && <div className="text-peoria-muted">Chaperones: {quote.chaperones}</div>}
            {quote.costCenter && <div className="text-peoria-muted">Cost center: {quote.costCenter}</div>}
          </div>
        </div>
      )}

      {(quote.equipment || quote.needsTrailer || quote.accessibilityNotes || quote.accessibilityRequested) && (
        <div className="mt-3">
          <div className="text-10 font-semibold text-peoria-muted uppercase tracking-brand mb-1">Equipment / accessibility</div>
          <div className="text-13 text-peoria-text space-y-0.5">
            {quote.equipment && <div>{quote.equipment}</div>}
            {quote.needsTrailer && <div className="text-peoria-muted">Trailer: {quote.needsTrailer}</div>}
            {quote.accessibilityNotes && <div className="text-peoria-muted">Accessibility: {quote.accessibilityNotes}</div>}
            {!quote.accessibilityNotes && quote.accessibilityRequested && <div className="text-peoria-muted">Accessibility need flagged</div>}
          </div>
        </div>
      )}

      {editing ? (
        <div className="mt-3 space-y-2">
          {[
            { key: 'base', label: 'Base charter rate' },
            { key: 'fuel', label: 'Fuel surcharge' },
            { key: 'extra', label: 'Extra fees' },
          ].map((row) => (
            <label key={row.key} className="flex items-center gap-3">
              <span className="flex-1 text-13 text-peoria-text">{row.label}</span>
              <div className="relative w-36 shrink-0">
                <span className="absolute left-2 top-1/2 -translate-y-1/2 text-13 text-peoria-muted font-mono">$</span>
                <input
                  aria-label={row.label}
                  className={cx(EDIT_INPUT, 'pl-5 text-right font-mono')}
                  inputMode="decimal"
                  value={draft[row.key]}
                  onChange={(e) => setField(row.key, e.target.value)}
                />
              </div>
            </label>
          ))}
          <div className="flex items-center gap-2 pt-1">
            <button type="button" onClick={applyEdit} className="px-3 py-1.5 rounded-btn text-13 font-semibold bg-peoria-red text-white hover:bg-peoria-red-dark">
              Re-price
            </button>
            <button type="button" onClick={() => setEditing(false)} className="px-3 py-1.5 rounded-btn text-13 font-medium text-peoria-muted border border-peoria-border hover:bg-peoria-surface2">
              Cancel
            </button>
          </div>
        </div>
      ) : (
        <div className="mt-3 divide-y divide-border/70">
          <div className="flex items-baseline justify-between py-2">
            <span className="text-13 text-peoria-text">Base charter rate</span>
            <span className="text-13 font-mono text-peoria-text whitespace-nowrap">{money(baseAmt)}</span>
          </div>
          <div className="flex items-baseline justify-between py-2">
            <span className="text-13 text-peoria-text">Fuel surcharge</span>
            <span className="text-13 font-mono text-peoria-text whitespace-nowrap">{money(fuelAmt)}</span>
          </div>
          <div className="flex items-center gap-2 py-2">
            <div className="flex-1 min-w-0">
              <div className="text-13 text-peoria-text">Extra fees</div>
              {extraDetail && !overrides && <div className="text-10 text-peoria-muted truncate">{extraDetail}</div>}
            </div>
            <span className="text-13 font-mono text-peoria-text whitespace-nowrap">{money(extraAmt)}</span>
          </div>
        </div>
      )}

      <div className="flex items-baseline justify-between py-3 mt-1 border-t border-peoria-border">
        <span className="text-11 font-medium text-peoria-muted uppercase tracking-brand">Estimated total</span>
        <span className="text-2xl font-bold text-peoria-text font-mono">{money(total)}</span>
      </div>
      <div className="flex items-baseline justify-between">
        <span className="text-11 font-medium text-peoria-muted uppercase tracking-brand">Deposit due now (15%)</span>
        <span className="text-base font-bold text-peoria-text font-mono">{money(deposit)}</span>
      </div>
      {quote.pricingDisclaimer && (
        <p className="mt-3 text-11 text-peoria-muted leading-relaxed">{quote.pricingDisclaimer}</p>
      )}
    </section>
  );
}

function FeasibilityBanner({ quote }) {
  const map = {
    FEASIBLE_SINGLE_DRIVER: { label: 'Single driver — within the 10-hr driving / 15-hr duty limits', cls: 'bg-green-500/10 border-green-500/30 text-green-700' },
    NEEDS_OVERNIGHT: { label: 'Overnight rest required', cls: 'bg-amber-500/10 border-amber-500/30 text-amber-700' },
    NEEDS_TEAM_DRIVER: { label: 'Team (second) driver required', cls: 'bg-danger/10 border-danger/40 text-red-600' },
  };
  const m = map[quote.feasibilityVerdict] || { label: quote.feasibilityVerdict || 'Feasibility', cls: 'bg-peoria-red/10 border-peoria-red/30 text-peoria-red' };
  return (
    <div className={cx('rounded-card border p-3', m.cls)}>
      <div className="text-13 font-semibold">DOT feasibility: {m.label}</div>
      {quote.feasibilityReason && <p className="text-11 mt-1 leading-relaxed">{quote.feasibilityReason}</p>}
      {(quote.driveHours != null || quote.onDutyHours != null) && (
        <div className="flex gap-4 mt-1.5 text-11 font-mono opacity-80">
          {quote.driveHours != null && <span>drive ~{Number(quote.driveHours).toFixed(1)}h</span>}
          {quote.onDutyHours != null && <span>on-duty ~{Number(quote.onDutyHours).toFixed(1)}h</span>}
          {quote.requiredRestMinutes > 0 && <span>required-rest ~{(quote.requiredRestMinutes / 60).toFixed(1)}h</span>}
        </div>
      )}
    </div>
  );
}

// ── driver-day HOS timeline (per-leg drive + DOT breaks + overnight) ──────────────
function HosTimeline({ quote }) {
  if (quote.outboundDriveHours == null) return null;
  const hrs = (v) => (v == null ? null : `${Number(v).toFixed(1)}h`);
  const brk = (m) => (m > 0 ? ` · +${m}m DOT rest` : '');
  const rows = [];
  if (quote.deadheadHours > 0) {
    rows.push({ label: `Deadhead to pickup & home ~${hrs(quote.deadheadHours)}`, sub: 'coach repositioning (dispatch)', tone: 'muted' });
  }
  rows.push({ label: `Outbound drive ~${hrs(quote.outboundDriveHours)}${brk(quote.outboundBreakMinutes)}` });
  if (quote.overnightNights > 0) {
    rows.push({ label: `Overnight × ${quote.overnightNights}`, sub: 'driver rest / lodging', tone: 'accent' });
  }
  if (quote.returnDriveHours > 0) {
    rows.push({ label: `Return drive ~${hrs(quote.returnDriveHours)}${brk(quote.returnBreakMinutes)}` });
  }
  return (
    <div className="mt-3">
      <div className="text-10 font-semibold text-peoria-muted uppercase tracking-brand mb-1.5">Driver day</div>
      <ol className="relative border-l-2 border-peoria-border/70 ml-1.5 space-y-2">
        {rows.map((r, i) => (
          <li key={i} className="ml-3 relative">
            <span className={cx('absolute -left-[1.15rem] top-1.5 w-2 h-2 rounded-full',
              r.tone === 'accent' ? 'bg-amber-500' : r.tone === 'muted' ? 'bg-peoria-border' : 'bg-peoria-red')} />
            <div className={cx('text-13', r.tone === 'muted' ? 'text-peoria-muted' : 'text-peoria-text')}>{r.label}</div>
            {r.sub && <div className="text-10 text-peoria-muted">{r.sub}</div>}
          </li>
        ))}
      </ol>
      {quote.requiredRestMinutes > 0 && (
        <p className="text-11 text-peoria-muted mt-1.5">
          Auto DOT rest folded into on-duty: ~{(quote.requiredRestMinutes / 60).toFixed(1)}h.
        </p>
      )}
    </div>
  );
}

// ── transcript panel ────────────────────────────────────────────────────────────
function TranscriptPanel({ transcript }) {
  return (
    <section className="bg-peoria-surface border border-peoria-border rounded-card shadow-card p-5">
      <h2 className="text-15 font-semibold text-peoria-text mb-3">Conversation transcript</h2>
      {(!transcript || transcript.length === 0) && (
        <p className="text-13 text-peoria-muted">No messages yet.</p>
      )}
      <div className="space-y-3 max-h-[520px] overflow-y-auto pr-1">
        {(transcript || []).map((t, i) => {
          const isUser = t.role === 'user';
          return (
            <div key={i} className="flex gap-2.5">
              <div className={cx('w-7 h-7 rounded-full flex items-center justify-center shrink-0',
                isUser ? 'bg-peoria-red/15 text-peoria-text' : 'bg-peoria-surface2 text-peoria-muted border border-peoria-border')}>
                {isUser ? <IconUser size={14} /> : <IconBot size={14} />}
              </div>
              <div className="min-w-0">
                <div className="text-10 uppercase tracking-brand text-peoria-muted">{isUser ? 'Customer' : 'Assistant'}</div>
                <p className="text-13 text-peoria-text leading-relaxed whitespace-pre-wrap break-words">{stripAgentTokens(t.text)}</p>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

// ── lead detail ─────────────────────────────────────────────────────────────────
function LeadDetail({ lead, onApprove, approving, approveNote, onReprice, repricing, editNote }) {
  if (!lead) {
    return (
      <div className="flex-1 flex items-center justify-center text-13 text-peoria-muted">
        Select a charter lead to review.
      </div>
    );
  }
  const state = parseState(lead);
  const values = state.values || {};
  const quote = leadQuote(lead);
  const isBooked = lead.status === 'BOOKED';
  const canApprove = !!quote && !isBooked;
  // A "unique trip" (special_request) is a plain team handoff — its whole ask is the free-text in the
  // SpecialRequestCard, so the structured "Information captured" box is noise. Hide it for those leads.
  const isUnique = String(values.trip_type || '').toLowerCase() === 'special_request'
    || !!(values.special_description || lead.specialDescription);
  return (
    <div className="flex-1 overflow-y-auto p-5">
      <div className="max-w-6xl mx-auto">
      <div className="flex items-center justify-between gap-3 mb-5 bg-peoria-surface border border-peoria-border rounded-card shadow-card px-5 py-3">
        <div className="flex items-center gap-3 min-w-0">
          <StatusPill status={lead.status} />
          <div className="min-w-0">
            <div className="text-13 font-semibold text-peoria-text truncate">
              {values.group_name || lead.groupName || 'Charter lead'}
            </div>
            <div className="text-11 text-peoria-muted">
              {isBooked
                ? 'Booked — reservation created.'
                : canApprove
                  ? 'Reviewed and ready — approve to create the booking.'
                  : 'No quote yet — collect the trip before approving.'}
            </div>
          </div>
        </div>
        <button
          onClick={onApprove}
          disabled={!canApprove || approving}
          className={cx(
            'shrink-0 px-4 py-2 rounded-btn text-13 font-semibold transition-colors',
            isBooked
              ? 'bg-green-500/10 text-green-700 border border-green-500/30 cursor-default'
              : canApprove
                ? 'bg-peoria-red text-white hover:bg-peoria-red-dark'
                : 'bg-peoria-surface2 text-peoria-muted border border-peoria-border cursor-not-allowed',
          )}
        >
          {isBooked
            ? <span className="inline-flex items-center gap-1.5"><IconCheck size={14} /> Booked</span>
            : approving ? 'Approving…' : 'Approve → create booking'}
        </button>
      </div>
      {approveNote && (
        <div className={cx(
          'mb-5 rounded-card border px-4 py-2.5 text-13',
          approveNote.type === 'ok'
            ? 'bg-green-500/10 border-green-500/30 text-green-700'
            : 'bg-danger/10 border-danger/40 text-red-600',
        )}>
          {approveNote.text}
        </div>
      )}
      {/* Below lg both column wrappers are display:contents, so all cards are direct grid items and
          fall in source order (mobile: customer, info, quote, transcript). At lg they become real
          columns — left: customer + info; right: quote with the conversation transcript beneath it. */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-5">
        <div className="contents lg:block lg:space-y-5">
          <CustomerCard values={values} lead={lead} />
          <SpecialRequestCard values={values} lead={lead} />
          {!isUnique && (
            <InformationCaptured
              key={lead.sessionId}
              values={values}
              onReprice={onReprice}
              busy={repricing}
              editNote={editNote}
            />
          )}
        </div>
        <div className="contents lg:block lg:space-y-5">
          <DraftQuotePanel
            key={lead.sessionId}
            quote={quote}
            editable={canApprove}
            quoteNumber={lead.quoteNumber}
          />
          <TranscriptPanel transcript={lead.transcript} />
        </div>
      </div>
      </div>
    </div>
  );
}

// ── app shell ───────────────────────────────────────────────────────────────────
function CharterDispatchApp({ authSession, userName, onLogout }) {
  const [leads, setLeads] = useState([]);
  const [query, setQuery] = useState('');
  const [selectedId, setSelectedId] = useState(null);
  const [detail, setDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [approving, setApproving] = useState(false);
  const [approveNote, setApproveNote] = useState(null);
  const [repricing, setRepricing] = useState(false);
  const [editNote, setEditNote] = useState(null);

  const refresh = useCallback(async () => {
    try {
      const data = await listCharterLeads(authSession);
      setLeads(Array.isArray(data) ? data : []);
      setError(null);
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [authSession]);

  useEffect(() => {
    refresh();
    const id = setInterval(refresh, REFRESH_MS);
    return () => clearInterval(id);
  }, [refresh]);

  useEffect(() => {
    if (!selectedId) { setDetail(null); return; }
    getCharterLead(selectedId, authSession).then(setDetail).catch((e) => setError(e.message));
  }, [selectedId, authSession]);

  // Clear any approve/edit message when the dispatcher switches leads.
  useEffect(() => { setApproveNote(null); setEditNote(null); }, [selectedId]);

  const approve = useCallback(async () => {
    if (!selectedId || approving) return;
    setApproving(true);
    setApproveNote(null);
    try {
      const booking = await approveCharterLead(selectedId, authSession);
      const ref = booking && booking.id ? ` #${booking.id}` : '';
      setApproveNote({ type: 'ok', text: `Booked — reservation${ref} created.` });
      await refresh();
      const fresh = await getCharterLead(selectedId, authSession);
      setDetail(fresh);
    } catch (e) {
      setApproveNote({
        type: 'err',
        text: e.status === 422 ? `Not bookable yet — ${e.message}` : e.message,
      });
    } finally {
      setApproving(false);
    }
  }, [selectedId, authSession, approving, refresh]);

  const reprice = useCallback(async (editPayload) => {
    if (!selectedId || repricing) return;
    setRepricing(true);
    setEditNote(null);
    try {
      const q = await editCharterLeadQuote(selectedId, editPayload, authSession);
      const total = q && q.totalAmount != null ? `$${Number(q.totalAmount).toFixed(2)}` : '';
      const verdict = q && q.feasibilityVerdict ? ` · ${q.feasibilityVerdict.replace(/_/g, ' ').toLowerCase()}` : '';
      setEditNote({ type: 'ok', text: `Re-priced — new total ${total}${verdict}.` });
      await refresh();
      const fresh = await getCharterLead(selectedId, authSession);
      setDetail(fresh);
    } catch (e) {
      setEditNote({ type: 'err', text: e.status === 422 ? `Couldn't re-price — ${e.message}` : e.message });
    } finally {
      setRepricing(false);
    }
  }, [selectedId, authSession, repricing, refresh]);

  // Client-side search over the leads rail. Matches the fields present on a summary row (group name,
  // the route+pax summary string, status, and the formatted total) — enough to find a lead/quote fast.
  const q = query.trim().toLowerCase();
  const visibleLeads = q
    ? leads.filter((l) => {
        const hay = [l.groupName, l.summary, l.status, l.quoteStatus, l.quoteNumber, money(l.totalAmount)]
          .filter(Boolean).join(' ').toLowerCase();
        return hay.includes(q);
      })
    : leads;

  return (
    <div className="h-screen flex flex-col bg-peoria-bg">
      <AppHeader
        userName={userName}
        onLogout={onLogout}
        subtitle="Quote Requests"
        logoSrc={CHARTER_LOGO}
        brandName="Peoria Charter"
        variant="peoria"
      />
      {error && (
        <div className="px-5 py-2 bg-danger/10 text-red-600 text-13 flex items-center gap-2">
          <IconWarning size={14} /> {error}
        </div>
      )}
      <div className="flex-1 flex min-h-0">
        <LeadsRail
          leads={visibleLeads}
          total={leads.length}
          query={query}
          onQuery={setQuery}
          selectedId={selectedId}
          onSelect={setSelectedId}
          loading={loading}
        />
        <LeadDetail
          lead={detail}
          onApprove={approve}
          approving={approving}
          approveNote={approveNote}
          onReprice={reprice}
          repricing={repricing}
          editNote={editNote}
        />
      </div>
      <button
        onClick={refresh}
        title="Refresh"
        className="fixed bottom-5 right-5 w-11 h-11 rounded-full bg-peoria-red text-white shadow-pop flex items-center justify-center hover:bg-peoria-red-dark transition-colors"
      >
        <IconRefresh size={18} />
      </button>
    </div>
  );
}

export default CharterDispatchApp;
export { DraftQuotePanel, leadQuote, HosTimeline, fmtGap, fmtPickups, fmtWhen, ItineraryLegs, legCount,
         ShuttleSummary, fmtServiceRange };   // exported for unit tests
// The recurrence/clock formatters moved to the shared ./shuttleFormat.js (used by the quote card too).
